import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, qs } from '../api/client';
import { Badge, Bandeau, Carte, Champ, Squelette, Vide } from '../components/ui';
import { ajouteJours, dateFr, isoJour, montant } from '../lib/format';
import type { Vehicule } from '../api/types';

interface Reponse {
  jours: number;
  message: string;
  tamponPreparationMin: number;
  disponibles: { vehicle: Vehicule; totalMinor: number; tarifJourMinor: number; saison: string }[];
  occupes: { vehicle: Vehicule; libereLe: string; detail: string }[];
}

/**
 * L'ecran le plus utilise : deux dates, une reponse.
 * Jamais de « non » sec — quand rien n'est libre, on dit quand ca se libere.
 */
export default function Disponibilites() {
  const [params, setParams] = useSearchParams();
  const [debut, setDebut] = useState(params.get('debut') ?? isoJour(new Date()));
  const [fin, setFin] = useState(params.get('fin') ?? isoJour(ajouteJours(new Date(), 7)));
  const [d, setD] = useState<Reponse | null>(null);
  const [chargement, setChargement] = useState(false);

  useEffect(() => {
    if (!debut || !fin || fin <= debut) return;
    setChargement(true);
    setParams({ debut, fin }, { replace: true });
    api.get<Reponse>(`/disponibilites${qs({ debut, fin })}`)
      .then(setD).finally(() => setChargement(false));
  }, [debut, fin, setParams]);

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>Voitures libres</h1>
          <p>Deux dates, la reponse. Le temps de preparation est déjà compte.</p>
        </div>
      </div>

      <Carte>
        <div className="grille g3" style={{ alignItems: 'end' }}>
          <Champ label="Du"><input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} /></Champ>
          <Champ label="Au"><input type="date" value={fin} min={debut} onChange={(e) => setFin(e.target.value)} /></Champ>
          <div className="mini sourdine">
            {d ? `${d.jours} jour(s) factures · tampon de preparation ${d.tamponPreparationMin} min` : ' '}
          </div>
        </div>
      </Carte>

      {chargement && <div style={{ marginTop: 16 }}><Squelette /></div>}

      {d && !chargement && (
        <>
          <div style={{ margin: '16px 0' }}>
            <Bandeau type={d.disponibles.length ? 'ok' : 'warn'} titre={d.disponibles.length ? `${d.disponibles.length} voiture(s) libre(s)` : 'Rien de libre sur cette période'}>
              {d.disponibles.length ? `Du ${dateFr(debut)} au ${dateFr(fin)}.` : d.message}
            </Bandeau>
          </div>

          {d.disponibles.length > 0 && (
            <Carte titre="Libres — prix calculé pour toute la période" pad={false}>
              <div className="enveloppe-tbl">
                <table className="tbl">
                  <thead>
                    <tr><th>Véhicule</th><th>Immatriculation</th><th>Catégorie</th><th className="droite">Tarif / jour</th><th className="droite">Total période</th><th /></tr>
                  </thead>
                  <tbody>
                    {d.disponibles.map((x) => (
                      <tr key={x.vehicle._id}>
                        <td><b>{x.vehicle.marque} {x.vehicle.modele}</b> <span className="sourdine mini">{x.vehicle.annee}</span></td>
                        <td className="num">{x.vehicle.immatriculation}</td>
                        <td className="mini sourdine">{x.vehicle.categorie}</td>
                        <td className="droite montant">{montant(x.tarifJourMinor)}<div className="mini sourdine" style={{ fontWeight: 400 }}>saison {x.saison}</div></td>
                        <td className="droite montant">{montant(x.totalMinor)}</td>
                        <td className="droite">
                          <Link className="btn btn-s btn-p" to={`/reservations/nouvelle?vehicle=${x.vehicle._id}&debut=${debut}&fin=${fin}`}>Réserver</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Carte>
          )}

          {d.occupes.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Carte titre="Prises — et quand elles se libèrent" pad={false}>
                <div className="enveloppe-tbl">
                  <table className="tbl compact">
                    <thead><tr><th>Véhicule</th><th>Immatriculation</th><th>Motif</th><th>Libre à partir du</th></tr></thead>
                    <tbody>
                      {d.occupes.sort((a, b) => +new Date(a.libereLe) - +new Date(b.libereLe)).map((o) => (
                        <tr key={o.vehicle._id}>
                          <td>{o.vehicle.marque} {o.vehicle.modele}</td>
                          <td className="num">{o.vehicle.immatriculation}</td>
                          <td className="mini sourdine">{o.detail}</td>
                          <td><Badge statut="retour" libelle={dateFr(o.libereLe)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Carte>
            </div>
          )}

          {d.disponibles.length === 0 && d.occupes.length === 0 && <Vide titre="Aucun véhicule dans la flotte" />}
        </>
      )}
    </>
  );
}
